import { isEmpty } from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { ProjectId } from 'api/models';
import { ProjectDetailTabKeys, tabApiEnumMap } from 'api/project';
import { VisibilityId, VisibilityMeta } from 'api/visibility';
import { VisibilityOptions } from 'components/molecules/visibility-options';
import { useProjectDetail } from 'data/projects';
import {
  useCreateProjectVisibility,
  useUpdateProjectVisibility,
} from 'hooks/visibility';
import { queueMacrotask } from 'utils/macrotask';
import { getProjectPlanUrl } from 'utils/urls';
import { useProjectVisibilityConfig } from '../../hooks';
import './visibility-overrides.css';
import {
  VisibilitySettingsWrapper,
  VisibilitySubMenuDesc,
  VisibilitySubMenuHeading,
  VisibilitySubMenuOption,
} from './visibility.styles';

interface Props {
  projectId: ProjectId;
  activeTabKey?: ProjectDetailTabKeys;
}

export const VisibilitySettings = ({ projectId, activeTabKey }: Props) => {
  const config = useProjectVisibilityConfig(projectId);
  const history = useHistory();

  const createProjectConfig = useCreateProjectVisibility(projectId);
  const updateProjectConfig = useUpdateProjectVisibility(projectId);

  const onChange = (payload: VisibilityMeta) => {
    if (config && !isEmpty(config)) {
      updateProjectConfig(payload, VisibilityId(config.visibilityId));
    } else {
      createProjectConfig(payload);
    }

    queueMacrotask(() => {
      if (activeTabKey) {
        // Navigate to plan page if the user disabled the active tab.
        const isActive = payload[tabApiEnumMap[activeTabKey]];

        // if user is in the same tab it would be become `false`. so adding a strict check here.
        // If user is in overview tab, it won't get disabled in vendor side. so don't navigate in that case.
        if (
          activeTabKey !== ProjectDetailTabKeys.Overview &&
          isActive === false
        ) {
          history.push(getProjectPlanUrl(projectId));
        }
      }
    });
  };

  const { data: project } = useProjectDetail(projectId);
  const customerName = project?.customer.companyName;

  return (
    <VisibilitySettingsWrapper
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <VisibilitySubMenuHeading>
        <FormattedMessage id="project.tabs.config.title" />
      </VisibilitySubMenuHeading>
      <VisibilitySubMenuDesc>
        <FormattedMessage
          id="project.tabs.config.desc"
          values={{ customerName }}
        />
      </VisibilitySubMenuDesc>
      <VisibilitySubMenuOption>
        <VisibilityOptions
          config={config}
          onOptionChange={onChange}
          financialContractType={project?.financialContractType}
        />
      </VisibilitySubMenuOption>
    </VisibilitySettingsWrapper>
  );
};
